<script lang="ts">
  import { getRadarImages, getLightningImage, getMapImage } from "../api.svelte";
  import { conditions, cType, lightningDelayError, lightningImageBase64String, mapImageBase64String, Observation, OrganizationLocation, organizationLocation, radarImageBase64Strings, Widget, widgetInfo, WidgetType } from "../store";
    import FeelsLikeCondition from "./conditions/FeelsLikeCondition.svelte";
    import GustCondition from "./conditions/GustCondition.svelte";
  import LightningTimer from "./LightningTimer.svelte";
  import StrikeTypeNote from "./StrikeTypeNote.svelte";
  import PrecipitationCondition from "./conditions/PrecipitationCondition.svelte";
  import TemperatureCondition from "./conditions/TemperatureCondition.svelte";
  import WbgtCondition from "./conditions/WBGTCondition.svelte";
  import WindCondition from "./conditions/WindCondition.svelte";
  import AqiCondition from "./conditions/AqiCondition.svelte";
    import { getPollutantTypeFromCondition } from "../aqiHelpers";

  export let type: cType = undefined;
  let widget: Widget;
  widgetInfo.subscribe((value: Widget) => {
    if (value)
    {
      widget = value;
    }
  });

  let orgLoc : OrganizationLocation;
  organizationLocation.subscribe((value: OrganizationLocation) => {
    if(value)
      orgLoc = value;
  });

  

  let innerWidth = 0;
  let innerHeight = 0;

  // Below this width the CG note doesn't fit beside the primary condition on IMG / RADAR,
  // so it moves to its own line under the row.
  const STRIKE_TYPE_ROW_MAX_WIDTH = 500;
  let isLightningError = false;
  lightningDelayError.subscribe((value: boolean) => {
    isLightningError = value;
  });
  $: strikeTypeOnRow = widget?.lightningStrikeType === "CG" && !isLightningError && innerWidth < STRIKE_TYPE_ROW_MAX_WIDTH;
  let containerWidth = 0;
  let containerHeight = 0;
  let currentRadarImage;
  $: {
    if(containerWidth > 0 && widget.type === WidgetType.RADAR){
      if(containerHeight == 0){
        //calc height based on width for 16:9 aspect ratio
        containerHeight = Math.floor(containerWidth * .5625);
      }
      if(containerWidth > 1920){
        let ratio = containerWidth / 1920.0;
        containerWidth = 1920;
        containerHeight = Math.floor(containerHeight / ratio);
      }
      getMapImage(orgLoc.latitude, orgLoc.longitude, widget.lightningRadius ?? 10,  containerWidth, containerHeight);
      getRadarImages(orgLoc.latitude, orgLoc.longitude, widget.lightningRadius ?? 10,  containerWidth, containerHeight);
      getLightningImage(orgLoc.latitude, orgLoc.longitude, widget.lightningRadius ?? 10,  containerWidth, containerHeight);
    }

    
  }

  let mapString: string;
  mapImageBase64String.subscribe((value: string) => {
    if(value)
    {
      mapString = value;
    }
  });

  let radarStrings: string[];
  radarImageBase64Strings.subscribe((value: string[]) => {
    if(value)
    {
      radarStrings = value;
      if(value && value.length > 0)
      {
        currentRadarImage = value[0];
      }
    }
  });

  let lightningString: string;
  lightningImageBase64String.subscribe((value: string) => {
    if(value)
    {
      lightningString = value;
    }
  });

  let imageContainer = document.getElementById("image-container");
 
  const imageBaseUrl = process.env.WIDGET_IMAGE_URL || "https://wx-image.perryweather.com";

  let wxsImageUrl;
  conditions.subscribe((value: Observation) => {
    const imageUrl = value?.weatherStation?.imageUrl;
    if (imageUrl) {
      const separator = imageUrl.includes("?") ? "&" : "?";
      wxsImageUrl = `${imageUrl}${separator}t=${new Date().getTime()}`;
    } else {
      wxsImageUrl = undefined;
    }
  });

  const conditionComponents = [ TemperatureCondition, WbgtCondition, WindCondition, PrecipitationCondition, GustCondition, FeelsLikeCondition, AqiCondition, AqiCondition, AqiCondition ]
  const conditionTypes = [ "Temperature", "WBGT", "Wind Speed", "Precipitation", "Wind Gust", "Feels Like", "PM2.5", "PM10", "Ozone", "AQI" ];
</script>

<svelte:window bind:innerWidth bind:innerHeight/>
<div class="image-container" bind:this={imageContainer} bind:clientHeight={containerHeight} bind:clientWidth={containerWidth}>
  {#if widget.type === WidgetType.IMG}
    <div
      class="image-props pw-flex pw-align-end"
      style={wxsImageUrl != undefined ? `background-image: url('${wxsImageUrl}');` : ""}
    >
    <div class="pw-flex pw-justify-between " class:strike-type-wrap={strikeTypeOnRow} style="width: 100%; background: linear-gradient(0deg, rgba(0,0,0,.85) 0%, rgba(255,255,255,0) 100%); border-radius: 10px; padding: 1vw;">

      <div class="primary-cc">
        {#if [6,7,8,9].includes(type)}
          <AqiCondition pollutantType={getPollutantTypeFromCondition(type)} isPrimaryCondition />
        {:else}
          <svelte:component this={conditionComponents[type]} isPrimaryCondition /> 
        {/if}
      </div>
      <div class="lightning-timer">
        <LightningTimer inlineStrikeType={!strikeTypeOnRow} />
      </div>  
      {#if strikeTypeOnRow}
        <StrikeTypeNote row />
      {/if}
    </div>
    </div>
  {/if}


  {#if widget.type === WidgetType.RADAR && mapString != undefined && currentRadarImage != undefined} 
    <div class="image-props pw-flex " style="background-image: url('data:image/png;base64,{mapString}'); position:relative;">
      <div 
        class="radar-image-props pw-flex pw-align-end pw-justify-between" 
        style="flex-grow: 1; background-image: url('data:image/png;base64,{currentRadarImage}'); opacity: .5; position: absolute; width: 100%"
      >
      </div>
      <div 
        class="radar-image-props pw-flex pw-align-end pw-justify-between" 
        style="flex-grow: 1; background-image: url('data:image/png;base64,{lightningString}'); opacity: 1; position: absolute; width: 100%"
      >
      </div>

      <div class="pw-flex pw-align-end" style="width: 100%; position:relative;">
        <div class="pw-flex pw-justify-between" class:strike-type-wrap={strikeTypeOnRow} style="width: 100%; background: linear-gradient(0deg, rgba(0,0,0,.85) 0%, rgba(255,255,255,0) 100%); border-radius: 10px;" >
          <div class="primary-cc">
            {#if [6,7,8,9].includes(type)}
              <AqiCondition pollutantType={getPollutantTypeFromCondition(type)} isPrimaryCondition />
            {:else}
              <svelte:component this={conditionComponents[type]} isPrimaryCondition /> 
            {/if}
          </div>
          <div class="lightning-timer">
              <LightningTimer inlineStrikeType={!strikeTypeOnRow} />
          </div>
          {#if strikeTypeOnRow}
            <StrikeTypeNote row />
          {/if}
        </div>  
      </div>
    </div>
  {/if}

  {#if widget.type === WidgetType.DEF} 
    <div class="image-props pw-flex pw-justify-around pw-align-center">
        <div class="primary-cc def-container">
          {#if [6,7,8,9].includes(type)}
            <AqiCondition pollutantType={getPollutantTypeFromCondition(type)} isPrimaryCondition />
          {:else}
            <svelte:component this={conditionComponents[type]} isPrimaryCondition /> 
          {/if}
        </div>
        <div class="lightning-timer def-container">
            <LightningTimer />
        </div>  
    </div>
  {/if}

  <style scoped>
  
    .primary-cc {
      line-height: 1;
      margin-bottom: 10px;
      margin-left: 10px;
      display: flex;
      align-items: flex-end;
      /* background-color: rgba(0,0,0,0.5); */
      /* border-radius: 20px; */
      /* padding: 10px */
    }
    .lightning-timer{
      /* background-color: rgba(0,0,0,0.5); */
      /* padding: 10px; */
      /* border-radius: 20px; */
    }
    
    .primary-cc-def {
      width: 25%;
      line-height: 1;
      margin-bottom: 10px;
    }
    .lightning-timer {
      line-height: 1;
      margin-bottom: 10px;
      margin-right: 10px;
      display: flex;
      align-items: center;
    }

    .strike-type-wrap {
      flex-wrap: wrap;
    }

    .image-container {
      flex-grow: 1;
      margin-bottom: 20px;
      border-radius: 10px;
    }
    .image-props {
      height: 100%;
      background-size: cover;
      background-position: center;
      background-repeat: no-repeat;
      border-radius: 10px;
      
    }
    .radar-image-props {
      height: 100%;
      background-size: 100% 100%;
      background-position: center;
      background-repeat: no-repeat;
      border-radius: 10px;
    }

    @media (max-width: 1080px) {
      .image-props {
        aspect-ratio: auto 16/9;
        height: auto;
        background-size: contain;
       
      }
      .radar-image-props {
        aspect-ratio: auto 16/9;
        height: auto;
        background-size: contain;
      }
    }

    

  </style>

</div>

